import {
  ApplicationConfig,
  importProvidersFrom,
  isDevMode,
  inject,
  provideAppInitializer,
  provideZoneChangeDetection,
} from '@angular/core';
import { provideRouter, Router } from '@angular/router';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { provideStore, Store } from '@ngrx/store';
import { provideEffects } from '@ngrx/effects';
import { provideStoreDevtools } from '@ngrx/store-devtools';
import { provideServiceWorker } from '@angular/service-worker';
import { SpeedTestModule } from 'ng-speed-test';

// Translation
import { provideTranslateService } from '@ngx-translate/core';
import { provideTranslateHttpLoader } from '@ngx-translate/http-loader';

// Store
import { reducers, metaReducers } from './store';

// Initializers
import { initializeUser, initializeWidget } from './core/initializers/user.initializer';
import { initializeApplication } from './core/initializers/app.initializer';
import { initializeFirebase } from './core/initializers/firebase.initializer';

import { WidgetEffects } from '@store/widget/widget.effects';
import { ProfileEffects } from '@store/profile/profile.effects';
import { ServicesEffects } from '@store/services/services.effects';
import { CallHistoryEffects } from '@store/call-history/call-history.effects';
import { CommonEffects } from '@store/common/common.effects';
import { PaymentsEffects } from '@store/payments/payments.effects';
import { LanguagesEffects } from '@store/languages/languages.effects';
import { SessionEffects } from '@store/session/session.effects';
import { TimezonesEffects } from '@store/timezones/timezones.effects';

// Interceptors
import { JwtInterceptor } from './core/interseptors/jwt.interceptor';
import { UrlInterceptor } from './core/interseptors/url.interceptor';
import { HttpErrorInterceptor } from './core/interseptors/http-error.interceptor';

// Services
import { WidgetsService } from '@services/widgets.service';
import { AuthService } from '@services/auth.service';
import { SvgService } from '@services/svg.service';
import { ThemeService } from '@services/theme/theme.service';
import { FirebaseService } from '@services/firebase.service';
import { I18nService } from '@services/i18n.service';

// Config
import { routes } from './app.routes';
import { messaging } from '../firebase.config';

export const appConfig: ApplicationConfig = {
  providers: [
    provideRouter(routes),
    provideHttpClient(withInterceptors([UrlInterceptor, HttpErrorInterceptor, JwtInterceptor])),
    provideStore(reducers, { metaReducers }),
    provideEffects(),
    provideEffects([
      WidgetEffects,
      ProfileEffects,
      ServicesEffects,
      CallHistoryEffects,
      CommonEffects,
      TimezonesEffects,
      PaymentsEffects,
      LanguagesEffects,
      SessionEffects,
    ]),
    provideStoreDevtools({
      maxAge: 25,
      logOnly: !isDevMode(),
    }),
    provideAnimationsAsync(),
    provideAppInitializer(() => {
      const initializerFn = initializeApplication(
        inject(SvgService),
        inject(ThemeService),
        inject(I18nService),
      );
      return initializerFn();
    }),
    provideAppInitializer(() => {
      const initializerFn = initializeUser(inject(AuthService), inject(Store), inject(Router));
      return initializerFn();
    }),
    provideAppInitializer(() => {
      const initializerFn = initializeWidget(inject(WidgetsService));
      return initializerFn();
    }),
    provideAppInitializer(() => {
      const initializerFn = initializeFirebase(inject(FirebaseService));
      return initializerFn();
    }),
    { provide: 'messaging', useValue: messaging },
    provideServiceWorker('/ngsw-worker.js', {
      enabled: !isDevMode(),
      // Register the ServiceWorker as soon as the app is stable
      // or after 30 seconds (whichever comes first).
      registrationStrategy: 'registerWhenStable:30000',
    }),
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideTranslateService({
      loader: provideTranslateHttpLoader({
        prefix: '/assets/i18n/',
        suffix: '.json',
      }),
      fallbackLang: 'en',
      lang: 'en',
    }),
    importProvidersFrom([SpeedTestModule]),
  ],
};
